import get from 'lodash/get'
import memoize from 'lodash/memoize'

import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import { showSnackbar } from 'utils/snackbar/actions'
import { SNACKBAR_ERROR, SNACKBAR_WARNING } from 'utils/snackbar/PositionedSnackbar'

/**
 * Pozi MDL Notifications Adapter
 */
const PoziNotifications = memoize(function () {
  // Adapters
  const adapters = {
    /**
     * @api {NA} PoziNotifications().privacy User privacy
     * @apiDescription Display privacy obligations to user in MDL snackbar based on layer configuration.
     * @apiGroup Notifications
     * @apiName privacy
     *
     * @apiParam {Object} layer Internal layer config
     *
     * @apiSuccess {Promise} Resolve Message displayed
     * @apiError {Promise} Reject Error occured displaying message
     */
    privacy: function (layer) {
      return privacy(layer)
    },
    /**
     * @api {NA} PoziNotifications().warning Warn user
     * @apiDescription Some warning message to display on GUI
     * @apiGroup Notifications
     * @apiName warning
     * @apiParam {String} message Some warning message to display on GUI
     * @apiParam {Object} optional options
     */
    warning: function (message, options) {
      return warning(message, options)
    },
    /**
     * @api {NA} PoziNotifications().error Notify user of error
     * @apiDescription Some error message to display on GUI
     * @apiGroup Notifications
     * @apiName error
     * @apiParam {String} message Some error message to display on GUI
     * @apiParam {Object} optional options
     */
    error: function (message, options) {
      return error(message, options)
    },
    /**
     * @api {NA} PoziNotifications().notify Notify user
     * @apiDescription Some notification message to display on GUI
     * @apiGroup Notifications
     * @apiName notify
     * @apiParam {String} message Some notification message to display on GUI
     */
    notify: function (message, action) {
      return notify(message, action)
    },

    /**
     * Quickly hide all notifications
     */
    hideAll: function () {
      return hideAll()
    }
  }

  /**
   * Handle privacy dialog
   * @param {object} layer config layer || null
   * @returns {promise} Resolves once dialog accept button has been clicked.
   */
  const privacy = function (layer) {
    return new Promise(function (resolve, reject) {
      if (!Model.get('app.LocalDataSourceDetected', false)) {
        return resolve('Skipping privacy message. No local data source detected.')
      }

      // If a layerConf is provided, we'll check for its privacy policy settings.
      // If not, then we are showing the policy dialog upon first load (only for a
      // site with a localDataSource)
      let bRequirePolicyAccept
      if (layer) {
        bRequirePolicyAccept = get(layer, 'privacy.policy', false)
      } else {
        bRequirePolicyAccept = true
      }

      if (!bRequirePolicyAccept) {
        return resolve('Skipping privacy message due to privacy policy flag being set to false.')
      }

      console.debug('Display privacy message to private users in mdl dialog box or snackbar.')

      let cookieName = 'privacy'
      if (layer) {
        cookieName += layer.title
      }
      const privacyCookie = PoziUtils.readCookie(cookieName)
      if (privacyCookie) {
        _showSnackbar(resolve, reject, 'Use of this site is subject to your organisation\'s privacy policy.')
      } else {
        console.debug('Create cookie to indicate privacy message viewed.')

        // Set cookie only when user accepts the policy
        const onAccept = () => {
          PoziUtils.createCookie(cookieName, 'true', 999)
          resolve(`Privacy policy accepted: ${cookieName}`)
        }

        showDialog(onAccept, reject, '#privacyDialog', '#dialogagree')
      }
    })
  }

  const warning = function (message, options = {}) {
    console.debug('Displaying warning message in snackbar: ', message)

    const snackbarOptions = {
      message,
      type: SNACKBAR_WARNING,
      ...options
    }
    window.store.dispatch(showSnackbar(snackbarOptions))
  }

  const error = function (message, options = {}) {
    console.debug('Displaying error message in snackbar: ', message)

    const snackbarOptions = {
      message,
      type: SNACKBAR_ERROR,
      ...options
    }
    window.store.dispatch(showSnackbar(snackbarOptions))
  }

  const notify = function (message, action = null) {
    console.debug('Displaying notification message in snackbar.', message)

    const snackbarOptions = (
      action
        ? { message, action }
        : { message }
    )
    window.store.dispatch(showSnackbar(snackbarOptions))
  }

  const _showSnackbar = function (resolve, reject, messageIn) {
    notify(messageIn)
    resolve('Message displayed in snackbar: ' + messageIn)
  }

  const showDialog = function (resolve, reject, dialogid, acceptbuttonid) {
    const dialog = document.querySelector(dialogid)
    window.dialogPolyfill.registerDialog(dialog) // See dialog-polyfill.min.js in index.html
    dialog.showModal()

    const options = {
      once: true
    }
    document.querySelector(acceptbuttonid).addEventListener(
      'click',
      function (evt) {
        evt.stopPropagation()
        console.log('Agree clicked', evt)
        document.querySelector(dialogid).close()
        resolve('Accept clicked')
      },
      options
    )
  }

  const hideAll = () => {
    const snackbarOptionsUpdate = { open: false }
    window.store.dispatch(showSnackbar(snackbarOptionsUpdate))
  }

  // Return adapters (must be at end of adapter)
  return adapters
})

export default PoziNotifications
